import type { LocalDict } from "../../core/i18n";

// Per-view translations for the Vault slice. Short, stable dot keys; {var}
// placeholders for interpolation. Technical identifiers (env var names, the
// ${vault:NAME} reference, the AES/PBKDF2 label, file paths) are intentionally
// left untranslated and live in the components as <code>.
export const STR: LocalDict = {
  en: {
    title: "Vault",
    "subtitle.prefix": "Encrypted credentials, API keys, and tokens. Reference them in agent.json5 with ",
    "subtitle.suffix": ".",

    "btn.refresh": "Refresh",
    "btn.refresh.title": "Refresh",
    "btn.changePassphrase": "Change passphrase",
    "btn.newSecret": "New secret",
    "btn.unlock": "Unlock vault",

    "status.title": "Encrypted secrets store",
    "status.badge.secrets": "{count} secret",
    "status.badge.secrets.plural": "{count} secrets",
    "status.unlocked": "unlocked",
    "status.locked": "locked",
    "status.key.env": "env OLANO_VAULT_KEY",
    "status.key.keyring": "system keyring",
    "status.key.ephemeral": "ephemeral key",
    "status.unlockHint": "Unlocking makes secrets available to the dashboard and all running agents for this session.",

    "empty.locked.title": "Vault is locked",
    "empty.locked.message": "Enter your vault passphrase to unlock. Agents cannot read secrets while locked.",
    "empty.error.title": "Couldn't load secrets",
    "empty.none.title": "No secrets yet",
    "empty.none.prefix": "Add API keys, tokens, or credentials. Reference them in agent.json5 with ",
    "empty.none.suffix": " instead of pasting them in plaintext.",
    "empty.none.action": "Add your first secret",

    "row.placeholder": "—",
    "row.updated": "updated {time}",
    "row.used": " · used {time}",
    "badge.expired": "expired",
    "badge.expired.title": "Expired {time}",
    "badge.expires": "expires {time}",
    "row.reveal": "Reveal value",
    "row.edit": "Edit",
    "row.delete": "Delete",
    "row.addOverride": "+ Override",
    "scope.common": "Common",
    "scope.agent": "agent: {agent}",
    "group.agentOnly": "Per-agent only",
    "secret.scope": "Scope",
    "secret.scope.common": "Common (all agents)",
    "secret.scope.override": "Override for {agent}",
    "secret.scope.editing": "Override for {agent}",
    "secret.scope.hint.common": "Shared by every agent unless overridden below.",
    "secret.scope.hint.override": "Only this agent uses this value; others fall back to the common one.",

    "toast.loadFailed": "Could not load secrets.",
    "toast.revealFailed": "Could not reveal secret.",
    "toast.deleted": "Secret deleted",
    "toast.deleteFailed": "Delete failed.",
    "toast.locked": "Vault is locked — unlock it so agents and Cortex can use your API keys.",

    // unlock modal
    "unlock.title": "Unlock vault",
    "unlock.subtitle": "Enter your passphrase to decrypt the secrets store.",
    "unlock.label": "Vault passphrase",
    "unlock.placeholder": "Enter passphrase…",
    "unlock.note.prefix": "Unlocking makes secrets available to the dashboard ",
    "unlock.note.bold": "and all running agents",
    "unlock.note.suffix": " for this server session. The vault re-locks on daemon restart unless you set ",
    "unlock.note.tail": " or use the system keyring.",
    "unlock.passphraseRequired": "Passphrase is required",
    "unlock.wrong": "Wrong passphrase",
    "unlock.failed": "Unlock failed.",
    "unlock.success": "Vault unlocked",
    "unlock.submitting": "Unlocking…",

    "btn.cancel": "Cancel",

    // secret drawer
    "secret.edit.title": "Edit {name}",
    "secret.new.title": "New secret",
    "secret.new.subtitle": "Stored encrypted at rest with AES-256-GCM.",
    "secret.name": "Name",
    "secret.name.hint.prefix": "UPPER_SNAKE_CASE convention. Reference as ",
    "secret.name.hint.suffix": ".",
    "secret.category": "Category",
    "secret.value": "Value",
    "secret.value.keepHint": "Leave blank to keep current",
    "secret.value.placeholder": "sk-… / ghp_… / token / passphrase",
    "secret.description": "Description",
    "secret.description.placeholder": "Used by the OpenAI provider in mark's agent.json5",
    "secret.expiresIn": "Expires in (days)",
    "secret.optional": "Optional",
    "secret.expiresIn.placeholder": "e.g. 90",
    "secret.rotation": "Rotation reminder (days)",
    "secret.nameRequired": "Name is required",
    "secret.valueRequired": "Value is required for new secrets",
    "secret.noChange": "No value change — left existing secret intact",
    "secret.updated": "Secret updated",
    "secret.stored": "Secret stored",
    "secret.saveFailed": "Save failed.",
    "secret.save": "Save changes",
    "secret.add": "Add secret",
    "field.show": "Show",
    "field.hide": "Hide",

    // rekey drawer
    "rekey.title": "Change vault passphrase",
    "rekey.current": "Current passphrase",
    "rekey.new": "New passphrase",
    "rekey.new.placeholder": "At least 8 characters",
    "rekey.confirm": "Confirm new passphrase",
    "rekey.warning.bold": "Warning:",
    "rekey.warning.prefix":
      " the entire vault is decrypted with the old key, re-encrypted with the new one, and replaced atomically. Existing keyring entries aren't updated automatically — set ",
    "rekey.warning.mid": " in your env or use ",
    "rekey.warning.suffix": " CLI to refresh.",
    "rekey.bothRequired": "Both passphrases are required",
    "rekey.mismatch": "New passphrases do not match",
    "rekey.tooShort": "New passphrase must be at least 8 characters",
    "rekey.done": "Re-encrypted {count} secret",
    "rekey.done.plural": "Re-encrypted {count} secrets",
    "rekey.failed": "Re-encryption failed.",
    "rekey.submit": "Re-encrypt vault",

    // reveal dialog
    "reveal.subtitle": "Decrypted value. Click 'Copy' to copy to clipboard, or close to dismiss.",
    "reveal.copied": "Copied — clears on send",
    "reveal.copyFailed": "Could not copy to clipboard.",
    "reveal.close": "Close",
    "reveal.copy": "Copy to clipboard",

    // delete confirm
    "delete.title": "Delete '{name}'?",
    "delete.message": "The secret '{name}' will be permanently removed from the vault. This cannot be undone.",
    "delete.deleting": "Deleting…",
    "delete.confirm": "Delete secret",

    // relative time
    "time.dash": "—",
    "time.inMoments": "in moments",
    "time.inMinutes": "in {n}m",
    "time.inHours": "in {n}h",
    "time.inDays": "in {n}d",
    "time.justNow": "just now",
    "time.minutesAgo": "{n}m ago",
    "time.hoursAgo": "{n}h ago",
    "time.daysAgo": "{n}d ago",
  },
  zh: {
    title: "保险库",
    "subtitle.prefix": "加密的凭据、API 密钥和令牌。在 agent.json5 中使用 ",
    "subtitle.suffix": " 引用它们。",

    "btn.refresh": "刷新",
    "btn.refresh.title": "刷新",
    "btn.changePassphrase": "修改口令",
    "btn.newSecret": "新建密钥",
    "btn.unlock": "解锁保险库",

    "status.title": "加密密钥存储",
    "status.badge.secrets": "{count} 个密钥",
    "status.badge.secrets.plural": "{count} 个密钥",
    "status.unlocked": "已解锁",
    "status.locked": "已锁定",
    "status.key.env": "环境变量 OLANO_VAULT_KEY",
    "status.key.keyring": "系统密钥环",
    "status.key.ephemeral": "临时密钥",
    "status.unlockHint": "解锁后，本次会话期间仪表盘和所有运行中的智能体都可访问密钥。",

    "empty.locked.title": "保险库已锁定",
    "empty.locked.message": "请输入保险库口令以解锁。锁定时智能体无法读取密钥。",
    "empty.error.title": "无法加载密钥",
    "empty.none.title": "暂无密钥",
    "empty.none.prefix": "添加 API 密钥、令牌或凭据。在 agent.json5 中使用 ",
    "empty.none.suffix": " 引用它们，而不是以明文粘贴。",
    "empty.none.action": "添加第一个密钥",

    "row.placeholder": "—",
    "row.updated": "更新于 {time}",
    "row.used": " · 使用于 {time}",
    "badge.expired": "已过期",
    "badge.expired.title": "已于 {time} 过期",
    "badge.expires": "{time} 过期",
    "row.reveal": "显示明文",
    "row.edit": "编辑",
    "row.delete": "删除",
    "row.addOverride": "+ 覆盖",
    "scope.common": "通用",
    "scope.agent": "代理：{agent}",
    "group.agentOnly": "仅限代理",
    "secret.scope": "范围",
    "secret.scope.common": "通用（所有代理）",
    "secret.scope.override": "为 {agent} 覆盖",
    "secret.scope.editing": "为 {agent} 覆盖",
    "secret.scope.hint.common": "除非在下方覆盖，否则所有代理共享。",
    "secret.scope.hint.override": "仅此代理使用此值；其他代理回退到通用值。",

    "toast.loadFailed": "无法加载密钥。",
    "toast.revealFailed": "无法显示密钥。",
    "toast.deleted": "密钥已删除",
    "toast.deleteFailed": "删除失败。",
    "toast.locked": "保险库已锁定 — 请解锁，以便智能体和认知模块使用你的 API 密钥。",

    "unlock.title": "解锁保险库",
    "unlock.subtitle": "输入口令以解密密钥存储。",
    "unlock.label": "保险库口令",
    "unlock.placeholder": "输入口令…",
    "unlock.note.prefix": "解锁后，本次服务器会话期间密钥将对仪表盘",
    "unlock.note.bold": "及所有运行中的智能体",
    "unlock.note.suffix": "可用。除非你设置 ",
    "unlock.note.tail": " 或使用系统密钥环，否则保险库会在守护进程重启时重新锁定。",
    "unlock.passphraseRequired": "口令为必填项",
    "unlock.wrong": "口令错误",
    "unlock.failed": "解锁失败。",
    "unlock.success": "保险库已解锁",
    "unlock.submitting": "正在解锁…",

    "btn.cancel": "取消",

    "secret.edit.title": "编辑 {name}",
    "secret.new.title": "新建密钥",
    "secret.new.subtitle": "使用 AES-256-GCM 静态加密存储。",
    "secret.name": "名称",
    "secret.name.hint.prefix": "采用 UPPER_SNAKE_CASE 命名规范。引用方式为 ",
    "secret.name.hint.suffix": "。",
    "secret.category": "分类",
    "secret.value": "值",
    "secret.value.keepHint": "留空则保持当前值",
    "secret.value.placeholder": "sk-… / ghp_… / 令牌 / 口令",
    "secret.description": "描述",
    "secret.description.placeholder": "由 mark 的 agent.json5 中的 OpenAI 提供商使用",
    "secret.expiresIn": "有效期（天）",
    "secret.optional": "可选",
    "secret.expiresIn.placeholder": "例如 90",
    "secret.rotation": "轮换提醒（天）",
    "secret.nameRequired": "名称为必填项",
    "secret.valueRequired": "新建密钥时值为必填项",
    "secret.noChange": "值未更改 — 已保留现有密钥",
    "secret.updated": "密钥已更新",
    "secret.stored": "密钥已保存",
    "secret.saveFailed": "保存失败。",
    "secret.save": "保存更改",
    "secret.add": "添加密钥",
    "field.show": "显示",
    "field.hide": "隐藏",

    "rekey.title": "修改保险库口令",
    "rekey.current": "当前口令",
    "rekey.new": "新口令",
    "rekey.new.placeholder": "至少 8 个字符",
    "rekey.confirm": "确认新口令",
    "rekey.warning.bold": "警告：",
    "rekey.warning.prefix":
      "整个保险库会用旧密钥解密，再用新密钥重新加密，并原子化替换。现有的密钥环条目不会自动更新 — 请在环境中设置 ",
    "rekey.warning.mid": "，或使用 ",
    "rekey.warning.suffix": " CLI 来刷新。",
    "rekey.bothRequired": "两个口令均为必填项",
    "rekey.mismatch": "两次输入的新口令不一致",
    "rekey.tooShort": "新口令至少需要 8 个字符",
    "rekey.done": "已重新加密 {count} 个密钥",
    "rekey.done.plural": "已重新加密 {count} 个密钥",
    "rekey.failed": "重新加密失败。",
    "rekey.submit": "重新加密保险库",

    "reveal.subtitle": "解密后的值。点击“复制”将其复制到剪贴板，或关闭以取消。",
    "reveal.copied": "已复制 — 发送后清除",
    "reveal.copyFailed": "无法复制到剪贴板。",
    "reveal.close": "关闭",
    "reveal.copy": "复制到剪贴板",

    "delete.title": "删除“{name}”？",
    "delete.message": "密钥“{name}”将从保险库中永久删除。此操作无法撤销。",
    "delete.deleting": "正在删除…",
    "delete.confirm": "删除密钥",

    "time.dash": "—",
    "time.inMoments": "片刻后",
    "time.inMinutes": "{n} 分钟后",
    "time.inHours": "{n} 小时后",
    "time.inDays": "{n} 天后",
    "time.justNow": "刚刚",
    "time.minutesAgo": "{n} 分钟前",
    "time.hoursAgo": "{n} 小时前",
    "time.daysAgo": "{n} 天前",
  },
  es: {
    title: "Bóveda",
    "subtitle.prefix": "Credenciales, claves de API y tokens cifrados. Refiérelos en agent.json5 con ",
    "subtitle.suffix": ".",

    "btn.refresh": "Actualizar",
    "btn.refresh.title": "Actualizar",
    "btn.changePassphrase": "Cambiar frase de acceso",
    "btn.newSecret": "Nuevo secreto",
    "btn.unlock": "Desbloquear bóveda",

    "status.title": "Almacén de secretos cifrado",
    "status.badge.secrets": "{count} secreto",
    "status.badge.secrets.plural": "{count} secretos",
    "status.unlocked": "desbloqueada",
    "status.locked": "bloqueada",
    "status.key.env": "env OLANO_VAULT_KEY",
    "status.key.keyring": "llavero del sistema",
    "status.key.ephemeral": "clave efímera",
    "status.unlockHint":
      "Al desbloquear, los secretos quedan disponibles para el panel y todos los agentes en ejecución durante esta sesión.",

    "empty.locked.title": "La bóveda está bloqueada",
    "empty.locked.message":
      "Introduce la frase de acceso de tu bóveda para desbloquearla. Los agentes no pueden leer los secretos mientras esté bloqueada.",
    "empty.error.title": "No se pudieron cargar los secretos",
    "empty.none.title": "Aún no hay secretos",
    "empty.none.prefix": "Añade claves de API, tokens o credenciales. Refiérelos en agent.json5 con ",
    "empty.none.suffix": " en lugar de pegarlos en texto plano.",
    "empty.none.action": "Añade tu primer secreto",

    "row.placeholder": "—",
    "row.updated": "actualizado {time}",
    "row.used": " · usado {time}",
    "badge.expired": "caducado",
    "badge.expired.title": "Caducó {time}",
    "badge.expires": "caduca {time}",
    "row.reveal": "Mostrar valor",
    "row.edit": "Editar",
    "row.delete": "Eliminar",
    "row.addOverride": "+ Anular",
    "scope.common": "Común",
    "scope.agent": "agente: {agent}",
    "group.agentOnly": "Solo por agente",
    "secret.scope": "Ámbito",
    "secret.scope.common": "Común (todos los agentes)",
    "secret.scope.override": "Anular para {agent}",
    "secret.scope.editing": "Anulación para {agent}",
    "secret.scope.hint.common": "Compartido por todos los agentes salvo que se anule abajo.",
    "secret.scope.hint.override": "Solo este agente usa este valor; los demás usan el común.",

    "toast.loadFailed": "No se pudieron cargar los secretos.",
    "toast.revealFailed": "No se pudo mostrar el secreto.",
    "toast.deleted": "Secreto eliminado",
    "toast.deleteFailed": "Error al eliminar.",
    "toast.locked": "La bóveda está bloqueada — desbloquéala para que los agentes y Córtex puedan usar tus claves de API.",

    "unlock.title": "Desbloquear bóveda",
    "unlock.subtitle": "Introduce tu frase de acceso para descifrar el almacén de secretos.",
    "unlock.label": "Frase de acceso de la bóveda",
    "unlock.placeholder": "Introduce la frase de acceso…",
    "unlock.note.prefix": "Al desbloquear, los secretos quedan disponibles para el panel ",
    "unlock.note.bold": "y todos los agentes en ejecución",
    "unlock.note.suffix": " durante esta sesión del servidor. La bóveda se vuelve a bloquear al reiniciar el daemon a menos que establezcas ",
    "unlock.note.tail": " o uses el llavero del sistema.",
    "unlock.passphraseRequired": "La frase de acceso es obligatoria",
    "unlock.wrong": "Frase de acceso incorrecta",
    "unlock.failed": "Error al desbloquear.",
    "unlock.success": "Bóveda desbloqueada",
    "unlock.submitting": "Desbloqueando…",

    "btn.cancel": "Cancelar",

    "secret.edit.title": "Editar {name}",
    "secret.new.title": "Nuevo secreto",
    "secret.new.subtitle": "Almacenado cifrado en reposo con AES-256-GCM.",
    "secret.name": "Nombre",
    "secret.name.hint.prefix": "Convención UPPER_SNAKE_CASE. Refiérelo como ",
    "secret.name.hint.suffix": ".",
    "secret.category": "Categoría",
    "secret.value": "Valor",
    "secret.value.keepHint": "Déjalo en blanco para conservar el actual",
    "secret.value.placeholder": "sk-… / ghp_… / token / frase de acceso",
    "secret.description": "Descripción",
    "secret.description.placeholder": "Usado por el proveedor OpenAI en el agent.json5 de mark",
    "secret.expiresIn": "Caduca en (días)",
    "secret.optional": "Opcional",
    "secret.expiresIn.placeholder": "p. ej. 90",
    "secret.rotation": "Recordatorio de rotación (días)",
    "secret.nameRequired": "El nombre es obligatorio",
    "secret.valueRequired": "El valor es obligatorio para los secretos nuevos",
    "secret.noChange": "Sin cambio de valor — se mantuvo el secreto existente",
    "secret.updated": "Secreto actualizado",
    "secret.stored": "Secreto guardado",
    "secret.saveFailed": "Error al guardar.",
    "secret.save": "Guardar cambios",
    "secret.add": "Añadir secreto",
    "field.show": "Mostrar",
    "field.hide": "Ocultar",

    "rekey.title": "Cambiar la frase de acceso de la bóveda",
    "rekey.current": "Frase de acceso actual",
    "rekey.new": "Nueva frase de acceso",
    "rekey.new.placeholder": "Al menos 8 caracteres",
    "rekey.confirm": "Confirma la nueva frase de acceso",
    "rekey.warning.bold": "Advertencia:",
    "rekey.warning.prefix":
      " toda la bóveda se descifra con la clave antigua, se vuelve a cifrar con la nueva y se reemplaza de forma atómica. Las entradas existentes del llavero no se actualizan automáticamente — establece ",
    "rekey.warning.mid": " en tu entorno o usa la CLI ",
    "rekey.warning.suffix": " para actualizarlas.",
    "rekey.bothRequired": "Ambas frases de acceso son obligatorias",
    "rekey.mismatch": "Las nuevas frases de acceso no coinciden",
    "rekey.tooShort": "La nueva frase de acceso debe tener al menos 8 caracteres",
    "rekey.done": "Se volvió a cifrar {count} secreto",
    "rekey.done.plural": "Se volvieron a cifrar {count} secretos",
    "rekey.failed": "Error al volver a cifrar.",
    "rekey.submit": "Volver a cifrar la bóveda",

    "reveal.subtitle": "Valor descifrado. Haz clic en 'Copiar' para copiarlo al portapapeles, o cierra para descartar.",
    "reveal.copied": "Copiado — se borra al enviar",
    "reveal.copyFailed": "No se pudo copiar al portapapeles.",
    "reveal.close": "Cerrar",
    "reveal.copy": "Copiar al portapapeles",

    "delete.title": "¿Eliminar '{name}'?",
    "delete.message": "El secreto '{name}' se eliminará permanentemente de la bóveda. Esto no se puede deshacer.",
    "delete.deleting": "Eliminando…",
    "delete.confirm": "Eliminar secreto",

    "time.dash": "—",
    "time.inMoments": "en un momento",
    "time.inMinutes": "en {n} min",
    "time.inHours": "en {n} h",
    "time.inDays": "en {n} d",
    "time.justNow": "ahora mismo",
    "time.minutesAgo": "hace {n} min",
    "time.hoursAgo": "hace {n} h",
    "time.daysAgo": "hace {n} d",
  },
};
